import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DoctorService} from '../../services/doctor/doctor.service';
import {Subject, takeUntil} from 'rxjs';
import {iResponse} from '../../models/response/response.model';
import {DoctorSettingsModel} from '../../models/doctor-settings/doctor-settings.model';
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {
    buildDoctorSettingsForm,
    patchDoctorSettingsToForm
} from '../../../utils/doctor-settings-form/doctor-settings-form.utils';
import {MatInput, MatSuffix} from '@angular/material/input';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import { NgClass } from '@angular/common';
import {CdkAccordion, CdkAccordionItem} from '@angular/cdk/accordion';
import {
    MatDatepickerToggle,
    MatDateRangeInput,
    MatDateRangePicker,
    MatEndDate,
    MatStartDate
} from '@angular/material/datepicker';
import {convertDateToISOString, convertTimeInputToTimestamp} from '../../../utils/date-time/date-time.utils';
import {
    AngularSignaturePadModule,
    NgSignaturePadOptions,
    SignaturePadComponent
} from '@almothafar/angular-signature-pad';
import {FileUploadComponent} from '../file-upload/file-upload.component';
import {InfoMessageService} from '../../services/infomessage/infomessage.service';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {DeviceDetectionService} from '../../services/device-detection/device-detection.service';

export const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'DD.MM.YYYY',
    },
    display: {
        dateInput: 'DD.MM.YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-administration-doctor-settings',
    imports: [
    FormsModule,
    MatInput,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    CdkAccordion,
    CdkAccordionItem,
    NgClass,
    MatDateRangeInput,
    MatDateRangePicker,
    MatDatepickerToggle,
    MatEndDate,
    MatStartDate,
    MatSuffix,
    AngularSignaturePadModule,
    FileUploadComponent
],
    templateUrl: './administration-doctor-settings.component.html',
    styleUrl: './administration-doctor-settings.component.sass'
})
export class AdministrationDoctorSettingsComponent implements OnInit, OnDestroy {
    @Input() doctorId: string = ''
    // @ts-ignore
    @ViewChild('signature') public signaturePad: SignaturePadComponent
    private destroy$ = new Subject<void>()

    doctorSettings: DoctorSettingsModel = {
        id: '',
        consultingHours: [],
        holidays: [],
        consultantDurationInMinutes: 0,
        treatmentCosts: 0,
        prescriptionCosts: 0,
        customTextForEmail: '',
        shippingCostsForPrescription: 0,
        aliacuraDebtCollectionFees: 0,
        aliacuraPortalFees: 0,
        aliacuraPostage: 0,
        signature: '',
        stamp: '',
        appointmentMaxToInWeeks: 0

    };

    doctorSettingsForm!: FormGroup;

    signaturePadOptions: NgSignaturePadOptions = {
        minWidth: 5,
        canvasWidth: 500,
        canvasHeight: 300
    };
    isMobile: boolean = false;

    constructor(
        private doctorService: DoctorService,
        private fb: FormBuilder,
        private infoMessageService: InfoMessageService,
        private deviceDetectionService: DeviceDetectionService

    ) {
        this.isMobile = deviceDetectionService.getIsMobile();
        this.doctorSettingsForm = buildDoctorSettingsForm(this.fb)
    }

    ngOnInit(): void {
        this.getDoctorSettings(this.doctorId);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Consulting hours
    get consultingHours(): FormArray {
        return this.doctorSettingsForm.get('consultingHours') as FormArray;
    }

    removeConsultingHour(index: number): void {
        this.consultingHours.removeAt(index);
    }

    triggerNewConsultingHour(): void {
        this.consultingHours.push(this.fb.group({
            day: ['', Validators.required],
            from: ['', Validators.required],
            to: ['', Validators.required]
        }));
    }


    // Holidays
    get holidays(): FormArray {
        return this.doctorSettingsForm.get('holidays') as FormArray;
    }

    removeHoliday(index: number): void {
        this.holidays.removeAt(index);
    }

    triggerNewHoliday(): void {
        this.holidays.push(this.fb.group({
            from: [Date, Validators.required],
            to: [Date, Validators.required]
        }));
    }

    //Signature
    drawComplete(): void {
        const signatureBase64 = this.signaturePad.toDataURL();
        this.doctorSettings.signature = signatureBase64.replace(/^data:image\/png;base64,/, '');
        this.doctorSettingsForm.get('signature')?.patchValue(this.doctorSettings.signature);
    }

    clearSignature(): void {
        this.signaturePad.clear();
        this.doctorSettingsForm.get('signature')?.patchValue('');
    }

    clearStamp(): void {
        this.doctorSettingsForm.get('stamp')?.patchValue('');
    }

    // File-Upload
    triggerFileUpload(base64: string): void {
        this.doctorSettingsForm.get('stamp')?.patchValue(base64);
    }

    submit(): void {
        if (this.doctorSettingsForm.invalid) {
            this.doctorSettingsForm.markAllAsTouched();
            return;
        }

        this.doctorSettings = this.doctorSettingsForm.value;

        this.doctorSettings.consultingHours = this.consultingHours.value.map((hour: any) => ({
            day: hour.day,
            from: convertTimeInputToTimestamp(hour.from),
            to: convertTimeInputToTimestamp(hour.to)
        }));

        this.doctorSettings.holidays = this.holidays.value.map((holiday: any) => ({
            from: convertDateToISOString(holiday.from),
            to: convertDateToISOString(holiday.to)
        }))

        this.saveDoctorSettings();
    }

    private getDoctorSettings(id: string): void {
        this.doctorService.getDoctorSettings(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<DoctorSettingsModel>) => {
                    this.doctorSettings = response.data;
                    patchDoctorSettingsToForm(this.fb, this.doctorSettingsForm, response.data);
                    this.loadSignatureToPad();
                },
                error: (error) => {
                }
            })
    }

    loadSignatureToPad(): void {
        const signature = this.doctorSettings.signature || this.doctorSettingsForm.get('signature')?.value;
        if (signature) {
            // Wir versuchen es mehrmals, da ViewChild in einem @if erst verzögert verfügbar sein kann
            let attempts = 0;
            const tryLoad = () => {
                if (this.signaturePad) {
                    try {
                        const dataUrl = signature.startsWith('data:')
                            ? signature
                            : `data:image/png;base64,${signature}`;
                        this.signaturePad.fromDataURL(dataUrl);
                    } catch (e) {
                        console.error('Error loading signature', e);
                    }
                } else if (attempts < 10) {
                    attempts++;
                    setTimeout(tryLoad, 100);
                }
            };
            tryLoad();
        }
    }

    private saveDoctorSettings(): void {
        this.doctorService.saveDoctorSettings(this.doctorId, this.doctorSettings)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Änderungen gespeichert', 'success');
                },
                error: (error) => {
                }
            })
    }
}
